import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import {
  getDataDashboard,
  getMetricsBdc,
  getMetricsMarketingOfUserCompanies,
  getMetricsMarketingOfUserCompaniesV2
} from '../../../graph-ql/dashboard/dashboard.queries';
import { getExternalSystemsByCompanyId } from '../../../graph-ql/externalIntegrations/externalIntegrations.queries';
import { changeCompany, changeConfigurationCompany } from '../../../graph-ql/mutations';
import {
  getAssigmentMethod,
  getCompanyById,
  getCompanyConfiguration,
  getUserLoggedCompanies
} from '../../../graph-ql/queries';
import { ChangeCompanyInput } from '../../models/company/changeCompany.input';
import { ChangeConfigurationInput } from '../../models/company/changeConfiguration.input';
import { GetMetricsMarketingTotalOfUserCompaniesResponse } from '../../models/company/companies.response';
import { Company } from '../../models/company/company.model';
import { CompanyConfigurationDto } from '../../models/company/companyConfiguration.model';
import { GetDataDashboardResponse } from '../../models/company/dashboard.response';
import { GetCompanyByIdInput } from '../../models/company/getCompanyById.input';
import { GetDataDashboardInput } from '../../models/company/getDataDashboard.input';
import { GetExternalSystemResponse } from '../../models/company/getExternalSystem.response';
import { GetMetricsMarketingTotalOfUserCompaniesInput } from '../../models/company/getMetricsMarketingTotalOfUserCompanies.input';
import { ExternalSystem } from '../../models/integration/externalSystem';
import { GetMetricsBdcInputDto } from '../../models/metrics/dashboards/getMetricBdcInputDto';
import { GetMetricsBdcResponse } from '../../models/metrics/dashboards/getMetricsBdc.response';
import { AuthService } from '../security/auth.service';

@Injectable()
export class CompanyService {

  private defaultCompanies = [
    '5021dc79-bf2d-46d8-959d-73e5da6e4a74',
  ];

  constructor(
    public apollo: Apollo,
    private authService: AuthService,
  ) {}

  getUserLoggedCompanies$(): Observable<GetMetricsMarketingTotalOfUserCompaniesResponse> {
    return this.apollo.query<GetMetricsMarketingTotalOfUserCompaniesResponse>({
      query: getUserLoggedCompanies
    }).pipe(map(result => {
      if (!result.data['getUserLoggedCompanies']) {
        return new GetMetricsMarketingTotalOfUserCompaniesResponse([]);
      }
      return new GetMetricsMarketingTotalOfUserCompaniesResponse(result.data['getUserLoggedCompanies']['companies']);
    }
    ));

  }

  getCompanyById$(getCompanyByIdInput: GetCompanyByIdInput): Observable<Company> {
    return this.apollo.query<Company>({
      query: getCompanyById,
      variables: {
        input: getCompanyByIdInput
      }
    }).pipe(map(result => {
      const companyDto = new Company();
      companyDto.id = result.data['getCompanyById'].id;
      companyDto.name = result.data['getCompanyById'].name;

      return companyDto;
    }
    ));
  }

  getCompanyConfiguration$(): Observable<CompanyConfigurationDto> {
    return this.apollo.query<CompanyConfigurationDto>({
      query: getCompanyConfiguration
    }).pipe(map(result => {
      const companyDto = new CompanyConfigurationDto();
      companyDto.timeZone = result.data['getCompanyConfiguration'].timeZone;
      companyDto.businessHours = result.data['getCompanyConfiguration'].businessHours;
      companyDto.businessHoursActive = result.data['getCompanyConfiguration'].businessHoursActived;
      companyDto.assignmentMethod = result.data['getCompanyConfiguration'].assignmentMethodType;
      companyDto.timeForConcurrence = result.data['getCompanyConfiguration'].timeForConcurrence;
      companyDto.trackingEmail = result.data['getCompanyConfiguration'].sendWithCustomEmail;
      companyDto.dropdownWithRules = result.data['getCompanyConfiguration'].dropdownWithRules;

      return companyDto;
    }
    ));
  }

  getAssigmentMethod$(): Observable<string> {
    return this.apollo.query<string>({
      query: getAssigmentMethod
    }).pipe(map(result => {

      return result.data['getAssigmentMethod'];
    }
    ));
  }

  changeCompany$(changeCompanyInput: ChangeCompanyInput): Observable<any> {
    return this.apollo.mutate<GetMetricsMarketingTotalOfUserCompaniesResponse>({
      mutation: changeCompany,
      variables: {
        input: changeCompanyInput
      }
    });
  }

  changeConfigurationCompany$(changeConfigurationCompanyInput: ChangeConfigurationInput): Observable<any> {
    return this.apollo.mutate<GetMetricsMarketingTotalOfUserCompaniesResponse>({
      mutation: changeConfigurationCompany,
      variables: {
        input: changeConfigurationCompanyInput
      }
    });
  }

  getDataDashboard$(
    getDataDashboardInput:
      GetDataDashboardInput): Observable<GetDataDashboardResponse> {
    return this.apollo.query<GetDataDashboardResponse>({
      query: getDataDashboard,
      variables: {
        input: getDataDashboardInput
      }
    }).pipe(map(result =>
      new GetDataDashboardResponse(
        result.data['getDataDashboard']['companies'])));
  }

  getMetricsMarketingOfUserCompanies$(
    getMetricsMarketingTotalOfUserCompaniesInput:
      GetMetricsMarketingTotalOfUserCompaniesInput): Observable<GetMetricsMarketingTotalOfUserCompaniesResponse> {
    return this.apollo.query<GetMetricsMarketingTotalOfUserCompaniesResponse>({
      query: getMetricsMarketingOfUserCompanies,
      variables: {
        input: getMetricsMarketingTotalOfUserCompaniesInput
      }
    }).pipe(map(result =>
      new GetMetricsMarketingTotalOfUserCompaniesResponse(
        result.data['getMetricsMarketingTotalOfUserCompanies']['companies'])));
  }

  getMetricsMarketingOfUserCompaniesV2$(
    getMetricsMarketingTotalOfUserCompaniesInput:
      GetMetricsMarketingTotalOfUserCompaniesInput): Observable<GetMetricsMarketingTotalOfUserCompaniesResponse> {
    return this.apollo.query<GetMetricsMarketingTotalOfUserCompaniesResponse>({
      query: getMetricsMarketingOfUserCompaniesV2,
      variables: {
        input: getMetricsMarketingTotalOfUserCompaniesInput
      }
    }).pipe(map(result =>
      new GetMetricsMarketingTotalOfUserCompaniesResponse(
        result.data['getMetricsMarketingTotalOfUserCompaniesV2']['companies'])));
  }

  getMetricsBdc$(getMetricsBdcInput: GetMetricsBdcInputDto): Observable<GetMetricsBdcResponse> {
    return this.apollo.query<GetMetricsBdcResponse>({
      query: getMetricsBdc,
      variables: {
        input: getMetricsBdcInput
      }
    }).pipe(map(result => new GetMetricsBdcResponse(result.data['getMetricsBdc']['companies'])));
  }

  getExternalSystemByCompanyId$(): Observable<GetExternalSystemResponse> {
    return this.apollo.query<GetExternalSystemResponse>({
      query: getExternalSystemsByCompanyId,
    }).pipe(map(result => {
      if (!result.data['getExternalSystemsByCompanyId']) {
        return new GetExternalSystemResponse(new Array<ExternalSystem>());
      }
      return new GetExternalSystemResponse(
        result.data['getExternalSystemsByCompanyId']['externalSystems']
      );
    }
    ));
  }

  isDefaultCompanyId(): boolean {
    const companyId = this.authService.getUser().company.id;
    return this.defaultCompanies.includes(companyId);
  }
}
